import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import {
  ButtonComponent,
  InputComponent,
  ModalData,
  ModalRef,
  SkeletonDirective,
} from '@ui-lib/components';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { WorkerByIdResponse } from '../../../../core/openapi/bff-admin-api';
import { LoadingState } from '../../../../shared/types/loading.type';
import { ClientSelectComponent } from '../../../../shared/components/client-select/client-select.component';
import { PoolSelectComponent } from '../../../../shared/components/pool-select/pool-select.component';
import { CoinSelectComponent } from '../../../../shared/components/coin-select/coin-select.component';
import { WorkerStatusSelectComponent } from '../../../../shared/components/worker-status-select/worker-status-select.component';
import { WorkerAction, IWorkerModalData } from '../../models';
import { WorkersService } from '../../workers.service';
import { WorkerFormControls } from './worker-modal.model';
import { PermissionService } from '../../../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../../../core/models/user-permissions.enum';

@Component({
  selector: 'mon-worker-modal',
  templateUrl: './worker-modal.component.html',
  styleUrl: './worker-modal.component.scss',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    InputComponent,
    SkeletonDirective,
    ClientSelectComponent,
    PoolSelectComponent,
    CoinSelectComponent,
    WorkerStatusSelectComponent,
  ],
  providers: [WorkersService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkerModalComponent extends ModalData<IWorkerModalData> implements OnInit {
  private readonly modalRef = inject(ModalRef<boolean>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);

  protected readonly workersService = inject(WorkersService);
  protected readonly WorkerAction = WorkerAction;
  protected readonly LoadingState = LoadingState;
  protected readonly canManageWorkers = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.WorkerManage),
    { initialValue: false },
  );
  protected readonly dataSectionTitle = '\u0414\u0430\u043d\u043d\u044b\u0435';
  protected readonly observerLinkSectionTitle =
    '\u0421\u0441\u044b\u043b\u043a\u0430 \u043d\u0430 observer';

  private loadedWorker: WorkerByIdResponse | null = null;

  protected readonly workerForm = new FormGroup<WorkerFormControls>({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    displayName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    client: new FormControl(null, { validators: [Validators.required] }),
    status: new FormControl(null, { validators: [Validators.required] }),
    pool: new FormControl(null, { validators: [Validators.required] }),
    coin: new FormControl(null, { validators: [Validators.required] }),
    observerLink: new FormControl('', { nonNullable: true }),
  });

  protected get isCreatingMode(): boolean {
    return this.modalData.action === WorkerAction.Create;
  }

  protected get isEditingMode(): boolean {
    return (
      this.modalData.action === WorkerAction.Edit &&
      this.modalData.workerId !== undefined &&
      this.modalData.workerId.length > 0
    );
  }

  protected get isInitialLoading(): boolean {
    return this.workersService.workerByIdLoadingStatus() === LoadingState.LOADING;
  }

  protected get isSubmitLoading(): boolean {
    return (
      this.workersService.workerCreateLoadingStatus() === LoadingState.LOADING ||
      this.workersService.workerUpdateLoadingStatus() === LoadingState.LOADING
    );
  }

  protected get isLoading(): boolean {
    return this.isInitialLoading || this.isSubmitLoading;
  }

  protected get modalTitleTranslationKey(): string {
    return this.isCreatingMode
      ? 'monitoring.workers.dashboard.add-worker'
      : 'monitoring.workers.modal.edit-title';
  }

  protected get submitTranslationKey(): string {
    return this.isCreatingMode
      ? 'monitoring.workers.modal.actions.create'
      : 'monitoring.workers.modal.actions.save';
  }

  public ngOnInit(): void {
    if (!this.isEditingMode || !this.modalData.workerId) {
      return;
    }

    this.workerForm.disable({ emitEvent: false });

    this.workersService
      .getWorkerById(this.modalData.workerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((worker) => {
        if (!worker) {
          this.modalRef.close(false);
          return;
        }

        this.loadedWorker = worker;
        this.patchForm(worker);
        this.workerForm.enable({ emitEvent: false });
        this.disableIdentityControls();
      });
  }

  protected submit(): void {
    if (!this.canManageWorkers()) {
      return;
    }

    this.workerForm.markAllAsTouched();

    if (this.workerForm.invalid || this.isLoading) {
      return;
    }

    if (this.isEditingMode && !this.loadedWorker) {
      return;
    }

    const value = this.workerForm.getRawValue();
    const name = value.name.trim();
    const displayName = value.displayName.trim();
    const observerLink = value.observerLink.trim();

    if (!value.client || !value.status || !value.pool || !value.coin) {
      return;
    }

    if (this.isCreatingMode && (name.length === 0 || displayName.length === 0)) {
      return;
    }

    const mutableRequest = {
      clientId: value.client.id,
      statusId: value.status.id,
      poolId: value.pool.id,
      coinId: value.coin.id,
      observerLink: observerLink.length > 0 ? observerLink : null,
    };

    const request$ = this.isCreatingMode
      ? this.workersService.createWorker({
          name,
          displayName,
          ...mutableRequest,
        })
      : this.workersService.updateWorker({
          id: this.loadedWorker?.id ?? this.modalData.workerId ?? '',
          ...mutableRequest,
        });

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result) {
        this.modalRef.close(true);
      }
    });
  }

  protected closeModal(): void {
    this.modalRef.close(false);
  }

  private patchForm(worker: WorkerByIdResponse): void {
    const currentTableWorker = this.modalData.worker;

    this.workerForm.patchValue(
      {
        name: worker.name ?? '',
        displayName: worker.displayName ?? '',
        client: worker.clientId
          ? {
              id: worker.clientId,
              label: worker.clientName ?? currentTableWorker?.clientName ?? worker.clientId,
            }
          : null,
        status: {
          id: worker.statusId,
          label:
            currentTableWorker?.status.code !== undefined
              ? this.translateService.instant(
                  'monitoring.workers.status.' + currentTableWorker.status.code,
                )
              : String(worker.statusId),
        },
        pool: worker.poolId
          ? {
              id: worker.poolId,
              label: currentTableWorker?.poolName || worker.poolId,
            }
          : null,
        coin:
          worker.coinId !== undefined && worker.coinId !== null
            ? {
                id: worker.coinId,
                label: currentTableWorker?.coin?.ticker || String(worker.coinId),
              }
            : null,
        observerLink: worker.observerLink ?? '',
      },
      { emitEvent: false },
    );
  }

  private disableIdentityControls(): void {
    this.workerForm.controls.name.disable({ emitEvent: false });
    this.workerForm.controls.displayName.disable({ emitEvent: false });
  }
}
